import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useRouteError } from 'react-router';

import { Button, Description, Text, dialogService } from '@thefarmersfront/kitchen-mobile';

import { EmptyText } from '~/components/shared';

/** @description 라우트 에러 처리 */
export const GlobalRouteErrorFallback = () => {
  const error = useRouteError();

  return <ErrorFallback error={error} />;
};

/** @description UI 에러 처리 */
export const GlobalErrorFallback = ({
  error,
  resetErrorBoundary,
}: {
  error: unknown;
  resetErrorBoundary?: () => void;
}) => {
  const { pathname } = useLocation();

  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    return () => {
      if (!isMounted.current) {
        return;
      }

      // 페이지 이탈 시 에러 리셋
      resetErrorBoundary?.();
    };
  }, [pathname]);

  return <ErrorFallback error={error} />;
};

const ErrorFallback = ({ error }: { error: unknown }) => {
  const navigate = useNavigate();

  const formattedError = error instanceof Error ? error : new Error(String(error));

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '30px',
        height: '100%',
      }}
    >
      <EmptyText text="예기치 못한\n에러가 발생했어요" />
      <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
        <Button
          color="tertiary"
          size="medium"
          onClick={() => {
            dialogService.addDialog({
              title: '에러 확인',
              children: (
                <>
                  <Text color="secondary">불편을 끼쳐 드려 죄송합니다.</Text>
                  <Text color="secondary">
                    해당 화면을 담당자에게 전달해 주시면 보다 신속하게 도움을 드릴 수 있습니다.
                  </Text>
                  <div style={{ marginTop: '8px', width: '100%' }} />
                  <Description
                    contents={[formattedError?.name, formattedError?.message]}
                    color="error"
                    showBullet={false}
                  />
                </>
              ),
              confirmText: '확인',
            });
          }}
        >
          에러 확인
        </Button>
        <Button
          size="medium"
          onClick={() => {
            navigate('/', { replace: true });
          }}
        >
          메인으로 이동
        </Button>
      </div>
    </div>
  );
};
